import type { Faq, JsonLd, Service } from '@/types'

import { absoluteUrl } from '@/lib/business'
import { services } from '@/constants/services'

import {
    address,
    areasServed,
    contact,
    hours,
    siteConfig
} from '@/constants/site'

const BUSINESS_ID = `${siteConfig.url}/#business`
const WEBSITE_ID = `${siteConfig.url}/#website`

const servedPlaces = (): JsonLd[] => {
    return areasServed.map(name => ({
        '@type': 'Place',
        name: name === 'North Delhi' ? 'North Delhi, Delhi' : `${name}, Delhi`
    }))
}

export const schemaGraph = (...nodes: JsonLd[]): JsonLd => {
    return {
        '@context': 'https://schema.org',
        '@graph': nodes
    }
}

export const localBusinessSchema = (): JsonLd => {
    return {
        '@type': 'BeautySalon',
        '@id': BUSINESS_ID,
        name: siteConfig.name,
        slogan: siteConfig.tagline,
        description: siteConfig.description,
        url: absoluteUrl('/'),
        telephone: contact.phoneSchema,
        image: [
            absoluteUrl('/images/hero-bride.jpg'),
            absoluteUrl('/images/salon-interior.jpg'),
            absoluteUrl('/images/nail-extensions.jpg')
        ],
        logo: absoluteUrl('/favicon.svg'),
        address: {
            '@type': 'PostalAddress',
            streetAddress: `${address.shop}, ${address.street}, ${address.locality}, ${address.area}`,
            addressLocality: address.city,
            addressRegion: address.region,
            postalCode: address.postalCode,
            addressCountry: address.country
        },
        hasMap: contact.mapsUrl,
        openingHoursSpecification: [
            {
                '@type': 'OpeningHoursSpecification',
                dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
                opens: hours.opens,
                closes: hours.closes
            }
        ],
        sameAs: [contact.instagramProfile],
        audience: {
            '@type': 'PeopleAudience',
            suggestedGender: 'female'
        },
        areaServed: servedPlaces(),
        knowsAbout: services.map(service => service.title),
        hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Beauty services',
            itemListElement: services.map(service => ({
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: service.name,
                    alternateName: service.title,
                    description: service.short,
                    url: absoluteUrl(`/services/${service.slug}`)
                }
            }))
        }
    }
}

export const websiteSchema = (): JsonLd => {
    return {
        '@type': 'WebSite',
        '@id': WEBSITE_ID,
        url: absoluteUrl('/'),
        name: siteConfig.name,
        description: siteConfig.description,
        inLanguage: 'en-IN',
        publisher: { '@id': BUSINESS_ID }
    }
}

export const faqSchema = (faqs: Faq[]): JsonLd => {
    return {
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer
            }
        }))
    }
}

export const serviceSchema = (service: Service): JsonLd => {
    const url = absoluteUrl(`/services/${service.slug}`)

    return {
        '@type': 'Service',
        '@id': `${url}#service`,
        name: service.name,
        alternateName: service.title,
        serviceType: service.title,
        description: service.description,
        image: absoluteUrl(service.cover),
        url,
        provider: { '@id': BUSINESS_ID },
        areaServed: servedPlaces(),
        audience: {
            '@type': 'PeopleAudience',
            suggestedGender: 'female'
        }
    }
}

export const serviceListSchema = (): JsonLd => {
    return {
        '@type': 'ItemList',
        name: `${siteConfig.name} services`,
        itemListElement: services.map((service, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: service.name,
            url: absoluteUrl(`/services/${service.slug}`)
        }))
    }
}

export const breadcrumbSchema = (items: { name: string, path: string }[]): JsonLd => {
    return {
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: absoluteUrl(item.path)
        }))
    }
}
